/* eslint max-len: "off" */
import * as React from 'react';
import { UserType } from 'src/graphql';
import { SYSTEM_PERMISSIONS } from 'src/permissionsTypes';
import {
  getAllUserPermissions,
  permissionsCheck,
} from 'src/utils/permissionChecks';
import { User } from '../../../types';
import DataIntegrationInfo from '../../atomsLegacy/DataIntegrationInfo';
import { TextBox } from '../../organismsLegacy/TextBox';
import { UserTextFieldsContainer } from './UserTextFields.styles';

interface Validation {
  name: boolean;
  email: boolean;
  role: boolean;
  department: boolean;
  site: boolean;
  eid: boolean;
  country: boolean;
  displayName: boolean;
}

export type UserTextFieldsProps = {
  /**
   * The user details that will be used in the textfields & edited.
   */
  user: User;
  /**
   * If a textfield is not valid, the error state will show on that textfield.
   */
  validation: Validation;
  /**
   * Handler when the textfields inputs are changed.
   */
  onTextboxChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >;
  /**
   * If the organisation has a data integration provider configured, all textfields should be disabled since user management will be handled by the provider.
   */
  disabled?: boolean;
};

/**
 * These user textboxes are used in the Edit User Modal.
 */

export const UserTextFields: React.FC<UserTextFieldsProps> = ({
  disabled = false,
  ...props
}: UserTextFieldsProps) => (
  <UserTextFieldsContainer>
    {disabled && <DataIntegrationInfo data-testid="data-integration-info" />}
    <TextBox
      data-testid="user-name"
      valid={props.validation.name}
      id="name"
      placeholder="Full Name"
      value={props.user.name}
      onChange={props.onTextboxChange}
      disabled={disabled}
    />

    {((props.user?.SystemRoles?.some(
      (role) => role.publicSystemPermissions?.canBeAssignedAsCaseLead,
    ) &&
      props.user?.SystemRoles?.some(
        (role) => role.publicSystemPermissions?.canBeAssignedToCaseTeam,
      )) ||
      (props.user
        ? permissionsCheck(getAllUserPermissions(props.user as UserType), [
            SYSTEM_PERMISSIONS.ASSIGNABLE_AS_CASE_LEAD,
            SYSTEM_PERMISSIONS.ASSIGNABLE_TO_CASE_TEAM,
          ])
        : false)) && (
      <TextBox
        data-testid="user-displayName"
        valid={props.validation.displayName}
        id="displayName"
        placeholder="Display Name (Optional)"
        value={props.user.displayName || ''}
        onChange={props.onTextboxChange}
        disabled={disabled}
      />
    )}
    <TextBox
      data-testid="user-email"
      valid={props.validation.email}
      id="email"
      placeholder="Email"
      value={props.user.email}
      onChange={props.onTextboxChange}
      disabled={disabled}
    />
    <TextBox
      data-testid="user-eid"
      valid={props.validation.eid}
      id="eid"
      placeholder="Employee Id"
      value={props.user.eid}
      onChange={props.onTextboxChange}
      disabled={disabled}
    />
    <TextBox
      data-testid="user-role"
      valid={props.validation.role}
      id="role"
      placeholder="Role"
      value={props.user.role}
      onChange={props.onTextboxChange}
      disabled={disabled}
    />
    <TextBox
      data-testid="user-department"
      valid={props.validation.department}
      id="department"
      placeholder="Department"
      value={props.user.department}
      onChange={props.onTextboxChange}
      disabled={disabled}
    />
    <TextBox
      data-testid="user-site"
      valid={props.validation.site}
      id="site"
      placeholder="Site"
      value={props.user.site}
      onChange={props.onTextboxChange}
      disabled={disabled}
    />
    <TextBox
      data-testid="user-country"
      valid={props.validation.country}
      id="country"
      placeholder="Country"
      value={props.user.country ?? ''}
      onChange={props.onTextboxChange}
      disabled={disabled}
    />
  </UserTextFieldsContainer>
);

export default UserTextFields;
